/**
 * The public knowledge graph over HTTP: the anonymous reads a public wiki
 * renders from, and the member/admin switch in project setup.
 *
 * The reads never send a token. The server builds what they return from an
 * allowlist (collabv3 `wiki/publicGraph.ts`); these types mirror it.
 */

import type { TeamJwt } from '../auth/teamJwt';
import { consoleConfig } from '../config';

export type PublicScalar = string | number | boolean;

/** No `itemId` means the target is not shared with the team; its title is then "Private page". */
export interface PublicReference {
  itemId?: string;
  title: string;
  issueKey?: string;
  trackerType?: string;
  relation?: string;
  qualifiers?: Record<string, PublicScalar | PublicScalar[]>;
}

export interface PublicItem {
  id: string;
  issueKey: string | null;
  type: string;
  title: string;
  labels: string[];
  fields: Record<string, unknown>;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface PublicTypeModel {
  type: string;
  displayName: string;
  displayNamePlural?: string;
  icon?: string;
  color?: string;
  roles: { title?: string; workflowStatus?: string };
  fields: Array<Record<string, unknown> & { name: string; type: string }>;
}

export interface PublicGraph {
  version: 1;
  types: PublicTypeModel[];
  predicates: Array<Record<string, unknown>>;
  labels: {
    labels: Array<Record<string, unknown>>;
    properties: Array<Record<string, unknown>>;
    claimProperties: Record<string, Record<string, unknown>>;
  } | null;
  items: PublicItem[];
  references: Array<{ id: string; title: string }>;
}

export interface PublicPage {
  item: PublicItem;
  body: string | null;
  references: Array<{ id: string; title: string }>;
}

export class PublicGraphError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = 'PublicGraphError';
  }
}

async function publicGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${consoleConfig.collabApiBaseUrl}${path}`, {
    credentials: 'omit',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!response.ok) {
    throw new PublicGraphError(response.status, response.status === 404 ? 'This wiki is not public.' : `Request failed with ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export async function fetchPublicGraph(slug: string, signal?: AbortSignal): Promise<PublicGraph> {
  const data = await publicGet<{ graph: PublicGraph }>(`/public/wiki/${encodeURIComponent(slug)}`, signal);
  return data.graph;
}

export async function fetchPublicPage(slug: string, idOrKey: string, signal?: AbortSignal): Promise<PublicPage> {
  const data = await publicGet<{ page: PublicPage }>(
    `/public/wiki/${encodeURIComponent(slug)}/pages/${encodeURIComponent(idOrKey)}`,
    signal,
  );
  return data.page;
}

// ---------------------------------------------------------------------------
// Member state and the admin switch
// ---------------------------------------------------------------------------

export interface PublicGraphSettings {
  public: boolean;
  slug: string | null;
  /** What becomes readable by anyone; an enable request must name exactly these. */
  visible: string[];
  /** What stays private. */
  hidden: string[];
}

function settingsPath(orgId: string, teamProjectId: string): string {
  return `/api/teams/${encodeURIComponent(orgId)}/projects/${encodeURIComponent(teamProjectId)}/public-graph`;
}

async function settingsRequest(jwt: TeamJwt, path: string, init: RequestInit = {}): Promise<PublicGraphSettings> {
  const response = await fetch(`${consoleConfig.collabApiBaseUrl}${path}`, {
    ...init,
    credentials: 'omit',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${jwt}`,
      ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
  });
  const data = await response.json().catch(() => null) as (PublicGraphSettings & { message?: string }) | null;
  if (!response.ok || !data) {
    throw new PublicGraphError(response.status, data?.message ?? `Request failed with ${response.status}`);
  }
  return data;
}

export function getPublicGraphSettings(jwt: TeamJwt, orgId: string, teamProjectId: string, signal?: AbortSignal): Promise<PublicGraphSettings> {
  return settingsRequest(jwt, settingsPath(orgId, teamProjectId), { signal });
}

/** Admin only; the server re-checks the role and the confirmation. */
export function enablePublicGraph(
  jwt: TeamJwt,
  orgId: string,
  teamProjectId: string,
  slug: string,
  confirmVisible: readonly string[],
): Promise<PublicGraphSettings> {
  return settingsRequest(jwt, settingsPath(orgId, teamProjectId), {
    method: 'PUT',
    body: JSON.stringify({ public: true, slug, confirmVisible }),
  });
}

export function disablePublicGraph(jwt: TeamJwt, orgId: string, teamProjectId: string): Promise<PublicGraphSettings> {
  return settingsRequest(jwt, settingsPath(orgId, teamProjectId), {
    method: 'PUT',
    body: JSON.stringify({ public: false }),
  });
}

/** Where a public graph is read, on this console's origin. */
export function publicWikiPath(slug: string, itemId?: string): string {
  return `/public/wiki/${encodeURIComponent(slug)}${itemId ? `/${encodeURIComponent(itemId)}` : ''}`;
}

/** A slug suggestion from a project name: lowercase words joined by single hyphens. */
export function suggestPublicSlug(name: string): string {
  const slug = name.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64);
  return slug.length >= 3 ? slug : `${slug || 'wiki'}-wiki`;
}
